import { create } from "zustand";
import { useMemo } from "react";
import { http } from "@/libs/http";
import { toast } from "sonner";
import type {
  Application,
  ClientSecretListItem,
  ClientSecretCreated,
  ClientSecretCreateRequest,
  ApiKeyListItem,
  ApiKeyCreated,
  ApiKeyCreateRequest,
  ApplicationHealth,
} from "@/types";
import type { PagenatedResponse } from "@/types/api";

interface ApplicationState {
  applications: Application[];
  isLoading: boolean;
  error: string | null;

  // Core CRUD
  list: (
    filter?: (apps: Application[]) => Application[],
  ) => Promise<Application[]>;
  get: (id: string) => Promise<Application>;
  create: (data: Partial<Application>) => Promise<void>;
  update: (id: string, data: Partial<Application>) => Promise<void>;
  delete: (id: string) => Promise<void>;
  select: (criteria: (apps: Application[]) => Application) => Application;

  // Tenant assignments (#119)
  listTenants: (appId: string) => Promise<any[]>;
  assignTenant: (appId: string, tenantId: string) => Promise<void>;
  unassignTenant: (appId: string, tenantId: string) => Promise<void>;

  // Health monitoring (#120)
  getHealth: (appId: string) => Promise<ApplicationHealth>;
  heartbeat: (
    appId: string,
    payload?: Record<string, unknown>,
  ) => Promise<ApplicationHealth>;

  // Client secrets (#124)
  listSecrets: (appId: string) => Promise<ClientSecretListItem[]>;
  createSecret: (
    appId: string,
    data: ClientSecretCreateRequest,
  ) => Promise<ClientSecretCreated>;
  revokeSecret: (appId: string, secretId: string) => Promise<void>;

  // Tenant-scoped API keys
  listApiKeys: (appId: string, tenantId: string) => Promise<ApiKeyListItem[]>;
  createApiKey: (
    appId: string,
    tenantId: string,
    data: ApiKeyCreateRequest,
  ) => Promise<ApiKeyCreated>;
  revokeApiKey: (
    appId: string,
    tenantId: string,
    keyId: string,
  ) => Promise<void>;

  // User / group (existing)
  listUsers: (appId: string) => Promise<any[]>;
  addUser: (appId: string, userId: string) => Promise<void>;
  removeUser: (appId: string, userId: string) => Promise<void>;
  listGroups: (appId: string) => Promise<any[]>;
  addGroup: (appId: string, groupId: string) => Promise<void>;
  removeGroup: (appId: string, groupId: string) => Promise<void>;
  listPermissions: (appId: string) => Promise<any[]>;
  addPermission: (appId: string, perm: string) => Promise<void>;
  removePermission: (appId: string, perm: string) => Promise<void>;
  listRoles: (appId: string) => Promise<any[]>;
  addRole: (appId: string, roleId: string) => Promise<void>;
  removeRole: (appId: string, roleId: string) => Promise<void>;
}

export const useApplications = create<ApplicationState>((set, get) => ({
  applications: [],
  isLoading: false,
  error: null,

  list: async (filter) => {
    set({ isLoading: true, error: null });
    try {
      const response = await http.get<PagenatedResponse<Application>>(
        "/mgmt/v1/applications",
      );
      const data = response.items;
      set({ applications: data, isLoading: false });
      return filter ? filter(data) : data;
    } catch (err: any) {
      const errorMsg = err.message || "Failed to load applications";
      set({ error: errorMsg, isLoading: false });
      toast.error(errorMsg);
      return [];
    }
  },

  get: async (id) => {
    const found = get().applications.find((app) => app.id === id);
    if (found) return found;
    const { data } = await http.get<{ data: Application }>(
      `/mgmt/v1/applications/${id}`,
    );
    set({
      applications: [...get().applications.filter((a) => a.id !== id), data],
    });
    return data;
  },

  create: async (data) => {
    const previous = get().applications;
    const tempId = `temp-${Date.now()}`;
    const tempApp = { ...data, id: tempId } as Application;
    set({ applications: [...previous, tempApp] });
    try {
      const result = await http.post<Application>(
        "/mgmt/v1/applications",
        data,
      );
      set({
        applications: get().applications.map((a) =>
          a.id === tempId ? result : a,
        ),
      });
      toast.success("Application created successfully");
    } catch (err: any) {
      const errorMsg = err.message || "Failed to create application";
      set({ applications: previous, error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },

  update: async (id, data) => {
    const previous = get().applications;
    set({
      applications: previous.map((a) => (a.id === id ? { ...a, ...data } : a)),
    });
    try {
      const result = await http.put<Application>(
        `/mgmt/v1/applications/${id}`,
        data,
      );
      set({
        applications: get().applications.map((a) => (a.id === id ? result : a)),
      });
      toast.success("Application updated successfully");
    } catch (err: any) {
      const errorMsg = err.message || "Failed to update application";
      set({ applications: previous, error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },

  delete: async (id) => {
    const previous = get().applications;
    set({ applications: previous.filter((a) => a.id !== id) });
    try {
      await http.delete(`/mgmt/v1/applications/${id}`);
      toast.success("Application deleted successfully");
    } catch (err: any) {
      const errorMsg = err.message || "Failed to delete application";
      set({ applications: previous, error: errorMsg });
      toast.error(errorMsg);
      throw err;
    }
  },

  select: (criteria) => criteria(get().applications),

  // Tenant assignments (#119)
  listTenants: async (appId) => {
    const response = await http.get(`/mgmt/v1/applications/${appId}/tenants`);
    return response.items || [];
  },
  assignTenant: async (appId, tenantId) => {
    await http.post(`/mgmt/v1/applications/${appId}/tenants`, { tenant_id: tenantId });
  },
  unassignTenant: async (appId, tenantId) => {
    await http.delete(`/mgmt/v1/applications/${appId}/tenants/${tenantId}`);
  },

  // Health monitoring (#120)
  getHealth: async (appId) =>
    http.get<ApplicationHealth>(`/mgmt/v1/applications/${appId}/health`),
  heartbeat: async (appId, payload = {}) =>
    http.post<ApplicationHealth>(
      `/mgmt/v1/applications/${appId}/heartbeat`,
      payload,
    ),

  // Client secrets (#124)
  listSecrets: async (appId) =>
    http.get<ClientSecretListItem[]>(`/mgmt/v1/applications/${appId}/secrets`),
  createSecret: async (appId, data) =>
    http.post<ClientSecretCreated>(
      `/mgmt/v1/applications/${appId}/secrets`,
      data,
    ),
  revokeSecret: async (appId, secretId) => {
    await http.delete(`/mgmt/v1/applications/${appId}/secrets/${secretId}`);
  },

  listApiKeys: async (appId, tenantId) =>
    http.get<ApiKeyListItem[]>(
      `/mgmt/v1/applications/${appId}/tenants/${tenantId}/api-keys`,
    ),
  createApiKey: async (appId, tenantId, data) =>
    http.post<ApiKeyCreated>(
      `/mgmt/v1/applications/${appId}/tenants/${tenantId}/api-keys`,
      data,
    ),
  revokeApiKey: async (appId, tenantId, keyId) => {
    await http.delete(
      `/mgmt/v1/applications/${appId}/tenants/${tenantId}/api-keys/${keyId}`,
    );
  },

  // User / group (existing)
  listUsers: async (appId) => http.get(`/mgmt/v1/applications/${appId}/users`),
  addUser: async (appId, userId) => {
    await http.post(`/mgmt/v1/applications/${appId}/users/${userId}`, {});
  },
  removeUser: async (appId, userId) => {
    await http.delete(`/mgmt/v1/applications/${appId}/users/${userId}`);
  },
  listGroups: async (appId) =>
    http.get(`/mgmt/v1/applications/${appId}/groups`),
  addGroup: async (appId, groupId) => {
    await http.post(`/mgmt/v1/applications/${appId}/groups/${groupId}`, {});
  },
  removeGroup: async (appId, groupId) => {
    await http.delete(`/mgmt/v1/applications/${appId}/groups/${groupId}`);
  },
  listPermissions: async (appId) =>
    http.get(`/mgmt/v1/applications/${appId}/permissions`),
  addPermission: async (appId, perm) => {
    await http.post(`/mgmt/v1/applications/${appId}/permissions/${perm}`, {});
  },
  removePermission: async (appId, perm) => {
    await http.delete(`/mgmt/v1/applications/${appId}/permissions/${perm}`);
  },
  listRoles: async (appId) => http.get(`/mgmt/v1/applications/${appId}/roles`),
  addRole: async (appId, roleId) => {
    await http.post(`/mgmt/v1/applications/${appId}/roles/${roleId}`, {});
  },
  removeRole: async (appId, roleId) => {
    await http.delete(`/mgmt/v1/applications/${appId}/roles/${roleId}`);
  },
}));

export const useApplication = (id?: string) => {
  const application = useApplications((state) =>
    id ? (state.applications.find((item) => item.id === id) ?? null) : null,
  );

  const ensureId = (): string => {
    if (!id) throw new Error("Application id is required.");
    return id;
  };

  return useMemo(() => {
    return {
      application,
      get: () => useApplications.getState().get(ensureId()),
      update: (data: Partial<Application>) =>
        useApplications.getState().update(ensureId(), data),
      delete: () => useApplications.getState().delete(ensureId()),
      tenants: {
        list: () => useApplications.getState().listTenants(ensureId()),
        assign: (tenantId: string) =>
          useApplications.getState().assignTenant(ensureId(), tenantId),
        unassign: (tenantId: string) =>
          useApplications.getState().unassignTenant(ensureId(), tenantId),
      },
      health: {
        get: () => useApplications.getState().getHealth(ensureId()),
        heartbeat: (payload?: Record<string, unknown>) =>
          useApplications.getState().heartbeat(ensureId(), payload),
      },
      secrets: {
        list: () => useApplications.getState().listSecrets(ensureId()),
        create: (data: ClientSecretCreateRequest) =>
          useApplications.getState().createSecret(ensureId(), data),
        revoke: (secretId: string) =>
          useApplications.getState().revokeSecret(ensureId(), secretId),
      },
      apiKeys: {
        list: (tenantId: string) =>
          useApplications.getState().listApiKeys(ensureId(), tenantId),
        create: (tenantId: string, data: ApiKeyCreateRequest) =>
          useApplications.getState().createApiKey(ensureId(), tenantId, data),
        revoke: (tenantId: string, keyId: string) =>
          useApplications.getState().revokeApiKey(ensureId(), tenantId, keyId),
      },
      users: {
        list: () => useApplications.getState().listUsers(ensureId()),
        create: (userId: string) =>
          useApplications.getState().addUser(ensureId(), userId),
        delete: (userId: string) =>
          useApplications.getState().removeUser(ensureId(), userId),
      },
      groups: {
        list: () => useApplications.getState().listGroups(ensureId()),
        create: (groupId: string) =>
          useApplications.getState().addGroup(ensureId(), groupId),
        delete: (groupId: string) =>
          useApplications.getState().removeGroup(ensureId(), groupId),
      },
      permissions: {
        list: () => useApplications.getState().listPermissions(ensureId()),
        create: (perm: string) =>
          useApplications.getState().addPermission(ensureId(), perm),
        delete: (perm: string) =>
          useApplications.getState().removePermission(ensureId(), perm),
      },
      roles: {
        list: () => useApplications.getState().listRoles(ensureId()),
        create: (roleId: string) =>
          useApplications.getState().addRole(ensureId(), roleId),
        delete: (roleId: string) =>
          useApplications.getState().removeRole(ensureId(), roleId),
      },
    };
  }, [id, application]);
};
